/**
 * Query options of the Analytics endpoints, one factory per endpoint with the
 * same `queryKey` the page always used, so every tab (and the current and
 * previous periods of `useWithPrevious`) share the React Query cache.
 */
import { queryOptions, type QueryKey } from '@tanstack/react-query';
import {
  analyticsControllerGetCampaignPerformance,
  analyticsControllerGetTenantAovChart,
  analyticsControllerGetTenantAppDownloadsChart,
  analyticsControllerGetTenantAppDownloadsCumulativeChart,
  analyticsControllerGetTenantAppSessionsChart,
  analyticsControllerGetTenantCampaignOrdersChart,
  analyticsControllerGetTenantDataSufficiencyMetrics,
  analyticsControllerGetTenantOrdersCompletion,
  analyticsControllerGetTenantRepurchaseRateChart,
  analyticsControllerGetTenantRevenueChart,
  analyticsControllerGetTenantSalesMetricsV2,
  analyticsControllerGetTenantUserMetrics,
  analyticsControllerGetTenantUsersInstalledVersionsMetrics,
} from '@/generated/korza-api-client';
import type { PredefinedRange } from '@/hooks/use-metrics-date-filter';
import type { ChartGranularity } from '@/lib/chart-axis';
import type { DateRange } from '@/lib/period-comparison';

/** Untagged key, so the options fit `useWithPrevious` as well as `useQuery`. */
const key = (...parts: unknown[]): QueryKey => parts;

/** Period and granularity of the Analytics filter, as the tabs receive it. */
export interface AnalyticsTabParams {
  fromDate: string | null;
  toDate: string | null;
  granularity: ChartGranularity;
  /** The filter's preset (`undefined` = custom), for the previous period. */
  preset?: PredefinedRange;
  /** `analyticsEnabled` of the store: off → nothing is fetched. */
  enabled: boolean;
}

export function salesMetricsQuery(tenantSlug: string, { from, to }: DateRange) {
  return queryOptions({
    queryKey: key('salesMetrics', tenantSlug, from, to),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantSalesMetricsV2({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function userMetricsQuery(tenantSlug: string, { from, to }: DateRange) {
  return queryOptions({
    queryKey: key('userMetrics', tenantSlug, from, to),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantUserMetrics({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function campaignPerformanceQuery(
  tenantSlug: string,
  { from, to }: DateRange
) {
  return queryOptions({
    queryKey: key('campaign-performance', tenantSlug, from, to),
    queryFn: async () => {
      const res = await analyticsControllerGetCampaignPerformance({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function ordersCompletionQuery(
  tenantSlug: string,
  { from, to }: DateRange
) {
  return queryOptions({
    queryKey: key('ordersCompletion', tenantSlug, from, to),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantOrdersCompletion({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function dataSufficiencyQuery(
  tenantSlug: string,
  { from, to }: DateRange
) {
  return queryOptions({
    queryKey: key('dataSufficiencyMetrics', tenantSlug, from, to),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantDataSufficiencyMetrics({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function revenueChartQuery(
  tenantSlug: string,
  { from, to }: DateRange,
  granularity: ChartGranularity
) {
  return queryOptions({
    queryKey: key('salesChart', tenantSlug, from, to, granularity),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantRevenueChart({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to, granularity },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function aovChartQuery(
  tenantSlug: string,
  { from, to }: DateRange,
  granularity: ChartGranularity
) {
  return queryOptions({
    queryKey: key('aovChart', tenantSlug, from, to, granularity),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantAovChart({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to, granularity },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function repurchaseChartQuery(
  tenantSlug: string,
  { from, to }: DateRange,
  granularity: ChartGranularity
) {
  return queryOptions({
    queryKey: key('repurchaseRateChart', tenantSlug, from, to, granularity),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantRepurchaseRateChart({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to, granularity },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function campaignOrdersChartQuery(
  tenantSlug: string,
  { from, to }: DateRange,
  granularity: ChartGranularity
) {
  return queryOptions({
    queryKey: key('campaignOrdersChart', tenantSlug, from, to, granularity),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantCampaignOrdersChart({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to, granularity },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function appDownloadsChartQuery(
  tenantSlug: string,
  { from, to }: DateRange,
  granularity: ChartGranularity
) {
  return queryOptions({
    queryKey: key('appDownloadsChart', tenantSlug, from, to, granularity),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantAppDownloadsChart({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to, granularity },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function appDownloadsCumulativeChartQuery(
  tenantSlug: string,
  { from, to }: DateRange,
  granularity: ChartGranularity
) {
  return queryOptions({
    queryKey: key(
      'appDownloadsCumulativeChart',
      tenantSlug,
      from,
      to,
      granularity
    ),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantAppDownloadsCumulativeChart(
        {
          path: { tenantSlug },
          query: { fromDate: from, toDate: to, granularity },
          throwOnError: true,
        }
      );
      return res.data;
    },
  });
}

export function appSessionsChartQuery(
  tenantSlug: string,
  { from, to }: DateRange,
  granularity: ChartGranularity
) {
  return queryOptions({
    queryKey: key('appSessionsChart', tenantSlug, from, to, granularity),
    queryFn: async () => {
      const res = await analyticsControllerGetTenantAppSessionsChart({
        path: { tenantSlug },
        query: { fromDate: from, toDate: to, granularity },
        throwOnError: true,
      });
      return res.data;
    },
  });
}

export function installedVersionsQuery(
  tenantSlug: string,
  { from, to }: DateRange
) {
  return queryOptions({
    queryKey: key('installedVersions', tenantSlug, from, to),
    queryFn: async () => {
      const res =
        await analyticsControllerGetTenantUsersInstalledVersionsMetrics({
          path: { tenantSlug },
          query: { fromDate: from, toDate: to },
          throwOnError: true,
        });
      return res.data;
    },
  });
}

/** The filter's period as the endpoints take it (empty while unresolved). */
export function tabRange({ fromDate, toDate }: AnalyticsTabParams): DateRange {
  return { from: fromDate ?? '', to: toDate ?? '' };
}
